'use strict';

import { webUsbDisponible, contextoSeguro, elegirDispositivo, dispositivosAutorizados, conectar, shellTexto } from './adb-connection.js';
import { crearLineaDuplex, instalarPuente } from './duplex-line.js';
import { FlowEngine } from './flow-engine.js';
import { CallMonitor } from './call-monitor.js';
import { guardar, leer } from './store.js';

const FLUJOS_DISPONIBLES = [
  'consultorio-medico', 'inmobiliaria', 'restaurante', 'soporte-tecnico', 'veterinaria',
  'prueba-1-audio', 'prueba-2-teclas', 'prueba-3-tiempos', 'prueba-4-grabadora',
];

const el = (id) => document.getElementById(id);

const ui = {
  avisoContexto: el('aviso-contexto'),
  btnConectar: el('btn-conectar'),
  btnReconectar: el('btn-reconectar'),
  estadoConexion: el('estado-conexion'),
  panelPuente: el('panel-puente'),
  inputDex: el('input-dex'),
  btnInstalarPuente: el('btn-instalar-puente'),
  estadoPuente: el('estado-puente'),
  panelLlamada: el('panel-llamada'),
  estadoLlamada: el('estado-llamada'),
  btnContestar: el('btn-contestar'),
  btnColgar: el('btn-colgar'),
  toggleAutoResponder: el('toggle-auto-responder'),
  segundosAutoResponder: el('segundos-auto-responder'),
  toggleGrabarLlamada: el('toggle-grabar-llamada'),
  panelFlujo: el('panel-flujo'),
  selectFlujo: el('select-flujo'),
  btnEjecutar: el('btn-ejecutar'),
  btnDetener: el('btn-detener'),
  listaNodos: el('lista-nodos'),
  log: el('log'),
};

let adb = null;
let linea = null;
let engine = null;
let monitor = null;
let flowActual = null;

function log(msg) {
  const hora = new Date().toLocaleTimeString();
  const p = document.createElement('div');
  p.className = 'log-linea';
  p.textContent = `[${hora}] ${msg}`;
  ui.log.appendChild(p);
  ui.log.scrollTop = ui.log.scrollHeight;
}

function setEstadoConexion(texto, clase) {
  ui.estadoConexion.textContent = texto;
  ui.estadoConexion.className = 'badge ' + (clase || '');
}

function setEstadoLlamada(texto, clase) {
  ui.estadoLlamada.textContent = texto;
  ui.estadoLlamada.className = 'badge ' + (clase || '');
}

// ---------- Comprobaciones de entorno ----------

function comprobarEntorno() {
  if (!contextoSeguro()) {
    ui.avisoContexto.hidden = false;
    ui.avisoContexto.textContent = 'Esta página no está en HTTPS (ni en localhost). WebUSB no va a funcionar hasta que la sirvas con HTTPS.';
    ui.btnConectar.disabled = true;
    return false;
  }
  if (!webUsbDisponible()) {
    ui.avisoContexto.hidden = false;
    ui.avisoContexto.textContent = 'Este navegador no soporta WebUSB. Abrí esta página con Chrome o Edge de escritorio.';
    ui.btnConectar.disabled = true;
    return false;
  }
  ui.avisoContexto.hidden = true;
  return true;
}

// ---------- Conexión ----------

async function alConectar() {
  try {
    const device = await elegirDispositivo();
    await iniciarConexion(device);
  } catch (err) {
    log('No se pudo conectar: ' + err.message);
  }
}

async function alReconectar() {
  const dispositivos = await dispositivosAutorizados();
  if (dispositivos.length === 0) {
    log('No hay ningún teléfono autorizado antes. Usá "Conectar teléfono".');
    return;
  }
  await iniciarConexion(dispositivos[0]);
}

async function iniciarConexion(device) {
  setEstadoConexion('Conectando…', 'ambar');
  try {
    adb = await conectar(device);
    setEstadoConexion(`Conectado: ${adb.serial}`, 'verde');
    log(`Conectado al teléfono ${adb.serial}.`);
    ui.panelPuente.hidden = false;
    ui.panelLlamada.hidden = false;
    ui.panelFlujo.hidden = false;

    linea = crearLineaDuplex({ adb, log });
    engine = new FlowEngine({
      audioBridge: linea.audioBridge,
      dtmf: linea.dtmf,
      phoneBridge: linea.phoneBridge,
    });
    engine.on('log', log);
    engine.on('active-node', marcarNodoActivo);
    engine.on('status', ({ running }) => {
      ui.btnEjecutar.disabled = running;
      ui.btnDetener.disabled = !running;
    });

    monitor = new CallMonitor({ adb, phoneBridge: linea.phoneBridge, log });
    monitor.on('cambio', ({ anterior, actual }) => {
      const nombres = { idle: 'En espera', ringing: 'Sonando', offhook: 'En curso' };
      const clases = { idle: '', ringing: 'ambar', offhook: 'verde' };
      setEstadoLlamada(nombres[actual] || actual, clases[actual]);
      if (engine && !engine.isRunning() && engine.shouldAutoRun(anterior, actual)) {
        log('Auto-arranque del flujo.');
        engine.run();
      }
    });
    monitor.start();

    adb.disconnected.then(() => {
      setEstadoConexion('Desconectado', 'rojo');
      log('El teléfono se desconectó.');
      monitor?.stop();
    });

    await intentarAutoInstalarPuente();
  } catch (err) {
    setEstadoConexion('Error de conexión', 'rojo');
    log('Error al autenticar con el teléfono: ' + err.message);
  }
}

// ---------- Instalación del puente (duplex.dex / mic.dex) ----------

async function intentarAutoInstalarPuente() {
  const dex = await leer('duplex.dex');
  if (dex) {
    ui.estadoPuente.textContent = 'duplex.dex guardado en este navegador — se reinstala solo.';
    await instalarDesdeCache(dex);
  } else {
    ui.estadoPuente.textContent = 'Falta subir duplex.dex una vez (ver instrucciones abajo).';
  }
}

async function instalarDesdeCache(duplexDexBytes) {
  try {
    const micResp = await fetch('assets/mic.dex');
    const micDexBytes = new Uint8Array(await micResp.arrayBuffer());
    await instalarPuente(adb, { duplexDexBytes, micDexBytes, log });
    ui.estadoPuente.textContent = 'Puente instalado ✓';
  } catch (err) {
    log('No se pudo instalar el puente: ' + err.message);
  }
}

async function alInstalarPuente() {
  const file = ui.inputDex.files[0];
  if (!file) { log('Elegí primero el archivo duplex.dex.'); return; }
  const bytes = new Uint8Array(await file.arrayBuffer());
  await guardar('duplex.dex', bytes);
  await instalarDesdeCache(bytes);
}

// ---------- Llamada ----------

async function alContestar() {
  try { await linea.phoneBridge.answer(); log('Contestado a mano.'); }
  catch (err) { log('No se pudo contestar: ' + err.message); }
}

async function alColgar() {
  try { await linea.phoneBridge.reject(); log('Colgado a mano.'); }
  catch (err) { log('No se pudo colgar: ' + err.message); }
}

function alCambiarAutoResponder() {
  monitor.configurarAutoResponder({
    activo: ui.toggleAutoResponder.checked,
    segundos: Number(ui.segundosAutoResponder.value) || 3,
  });
}

function alCambiarGrabarLlamada() {
  if (ui.toggleGrabarLlamada.checked) linea.iniciarGrabacionLlamada();
  else linea.detenerGrabacionLlamada();
}

// ---------- Flujos ----------

async function cargarFlujo(nombre) {
  const resp = await fetch(`flujos/${nombre}.json`);
  flowActual = await resp.json();
  engine.setFlow(flowActual);
  renderNodos(flowActual);
  log(`Flujo cargado: ${nombre}`);
}

function renderNodos(flow) {
  ui.listaNodos.innerHTML = '';
  for (const nodo of flow.nodes) {
    const li = document.createElement('li');
    li.dataset.id = nodo.id;
    li.className = 'nodo';
    const detalle = nodo.data && nodo.data.file ? nodo.data.file.split('/').pop()
      : nodo.data && nodo.data.timeoutMs ? `${nodo.data.timeoutMs / 1000}s`
      : nodo.data && nodo.data.seconds ? `${nodo.data.seconds}s`
      : nodo.data && nodo.data.ms ? `${nodo.data.ms / 1000}s`
      : '';
    li.innerHTML = `<span class="nodo-tipo">${nodo.type}</span><span class="nodo-id">${nodo.id}</span><span class="nodo-detalle">${detalle}</span>`;
    ui.listaNodos.appendChild(li);
  }
}

function marcarNodoActivo(nodeId) {
  for (const li of ui.listaNodos.children) {
    li.classList.toggle('activo', li.dataset.id === nodeId);
  }
}

// ---------- Arranque ----------

function poblarSelectorFlujos() {
  for (const nombre of FLUJOS_DISPONIBLES) {
    const opt = document.createElement('option');
    opt.value = nombre;
    opt.textContent = nombre;
    ui.selectFlujo.appendChild(opt);
  }
}

function conectarEventos() {
  ui.btnConectar.addEventListener('click', alConectar);
  ui.btnReconectar.addEventListener('click', alReconectar);
  ui.btnInstalarPuente.addEventListener('click', alInstalarPuente);
  ui.btnContestar.addEventListener('click', alContestar);
  ui.btnColgar.addEventListener('click', alColgar);
  ui.toggleAutoResponder.addEventListener('change', alCambiarAutoResponder);
  ui.segundosAutoResponder.addEventListener('change', alCambiarAutoResponder);
  ui.toggleGrabarLlamada.addEventListener('change', alCambiarGrabarLlamada);
  ui.selectFlujo.addEventListener('change', () => cargarFlujo(ui.selectFlujo.value));
  ui.btnEjecutar.addEventListener('click', () => engine.run());
  ui.btnDetener.addEventListener('click', () => engine.stop());
}

(function iniciar() {
  poblarSelectorFlujos();
  conectarEventos();
  if (comprobarEntorno()) {
    dispositivosAutorizados().then((ds) => { ui.btnReconectar.disabled = ds.length === 0; });
  }
})();
